import {
  Box,
  Fade,
  FormControlLabel,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material'
import ListItemDefault from '../../../../../components/ListItem/ListItemDefault'
import SwitchRoundSmall from '../../../../../components/Switch/SwitchRoundSmall'
import InfoInputAdornment from '../../../../../components/Inputs/InfoInputAdornment'
import { HtmlTooltip } from '../../../../../components/Tooltip/HtmlTooltip'
import { red } from '@mui/material/colors'
import { maskMoney } from '../../../../../masks/masks'
import NumberFunctions from '../../../../../core/functions/NumberFunctions'
import { useAdminUserContext } from '../../../../../core/hooks/useAdminUserContext'
import { UserSettingSecurityProps } from '../UserSettingTypes'

const UserSettingSecurity = ({
  errorMaxCardAttemptsPerHour,
  errorCardAttemptsWindowInMinutes,
  errorMaxDocumentTransactionsPerDay,
  errorDocumentTransactionsWindowInHours,
  errorConsecutiveCardDeclinesToBlock,
  errorCardTemporaryBlockMinutes,
  errorDailyCardAmountLimit,
  setErrorMaxCardAttemptsPerHour,
  setErrorCardAttemptsWindowInMinutes,
  setErrorMaxDocumentTransactionsPerDay,
  setErrorDocumentTransactionsWindowInHours,
  setErrorConsecutiveCardDeclinesToBlock,
  setErrorCardTemporaryBlockMinutes,
  setErrorDailyCardAmountLimit,
}: UserSettingSecurityProps) => {
  const { tenantSettings, setTenantSettings } = useAdminUserContext()

  const handleChangeInt =
    (
      field: string,
      setError: (value: string) => void
    ) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!tenantSettings) return
      setError('')
      const value = parseInt(e.target.value, 10) || 0
      setTenantSettings({ ...tenantSettings, [field]: value })
    }

  const handleChangeDailyCardAmountLimit = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!tenantSettings) return
    setErrorDailyCardAmountLimit('')
    setTenantSettings({
      ...tenantSettings,
      dailyCardAmountLimit: e.target.value as any,
    })
  }

  return (
    <Stack direction="column" spacing={2} sx={{ py: 1 }}>
      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Segurança de pagamento
        </Typography>

        <Stack spacing={1.5}>
          <ListItemDefault
            title={
              <HtmlTooltip title="Quando ativado, transações de cartão de crédito passarão pelo fluxo de autenticação 3D Secure via Pagar.me.">
                <FormControlLabel
                  control={
                    <SwitchRoundSmall
                      sx={{ m: 1 }}
                      checked={tenantSettings?.enablePagarme3DS ?? false}
                      onChange={(e) => {
                        if (!tenantSettings) return
                        setTenantSettings({
                          ...tenantSettings,
                          enablePagarme3DS: e.target.checked,
                        })
                      }}
                    />
                  }
                  label={
                    <Typography variant="subtitle2">
                      Habilitar 3D Secure (Pagar.me)
                    </Typography>
                  }
                  sx={{ width: '100%' }}
                />
              </HtmlTooltip>
            }
            widthTitlePercentage={50}
            variant="compact"
            value={undefined}
          />

          <ListItemDefault
            title={
              <HtmlTooltip title="Quando ativado, o sistema coletará e armazenará a impressão digital do dispositivo do comprador para análise antifraude.">
                <FormControlLabel
                  control={
                    <SwitchRoundSmall
                      sx={{ m: 1 }}
                      checked={
                        tenantSettings?.enableDeviceFingerprint ?? false
                      }
                      onChange={(e) => {
                        if (!tenantSettings) return
                        setTenantSettings({
                          ...tenantSettings,
                          enableDeviceFingerprint: e.target.checked,
                        })
                      }}
                    />
                  }
                  label={
                    <Typography variant="subtitle2">
                      Habilitar Device Fingerprint
                    </Typography>
                  }
                  sx={{ width: '100%' }}
                />
              </HtmlTooltip>
            }
            widthTitlePercentage={50}
            variant="compact"
            value={undefined}
          />

          <ListItemDefault
            title={
              <HtmlTooltip title="Quando ativado, regras de velocidade serão aplicadas para limitar tentativas de pagamento suspeitas.">
                <FormControlLabel
                  control={
                    <SwitchRoundSmall
                      sx={{ m: 1 }}
                      checked={tenantSettings?.enableVelocityCheck ?? false}
                      onChange={(e) => {
                        if (!tenantSettings) return
                        setTenantSettings({
                          ...tenantSettings,
                          enableVelocityCheck: e.target.checked,
                        })
                      }}
                    />
                  }
                  label={
                    <Typography variant="subtitle2">
                      Habilitar Velocity Check
                    </Typography>
                  }
                  sx={{ width: '100%' }}
                />
              </HtmlTooltip>
            }
            widthTitlePercentage={50}
            variant="compact"
            value={undefined}
          />
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Carteiras digitais
        </Typography>

        <Stack spacing={1.5}>
          <ListItemDefault
            title={
              <HtmlTooltip title="Quando ativado, o comprador poderá pagar pelo checkout usando o Google Pay.">
                <FormControlLabel
                  control={
                    <SwitchRoundSmall
                      sx={{ m: 1 }}
                      checked={tenantSettings?.enableGooglePay ?? false}
                      onChange={(e) => {
                        if (!tenantSettings) return
                        setTenantSettings({
                          ...tenantSettings,
                          enableGooglePay: e.target.checked,
                        })
                      }}
                    />
                  }
                  label={
                    <Typography variant="subtitle2">
                      Habilitar Google Pay
                    </Typography>
                  }
                  sx={{ width: '100%' }}
                />
              </HtmlTooltip>
            }
            widthTitlePercentage={50}
            variant="compact"
            value={undefined}
          />

          <ListItemDefault
            title={
              <HtmlTooltip title="Quando ativado, o comprador poderá pagar pelo checkout usando o Apple Pay.">
                <FormControlLabel
                  control={
                    <SwitchRoundSmall
                      sx={{ m: 1 }}
                      checked={tenantSettings?.enableApplePay ?? false}
                      onChange={(e) => {
                        if (!tenantSettings) return
                        setTenantSettings({
                          ...tenantSettings,
                          enableApplePay: e.target.checked,
                        })
                      }}
                    />
                  }
                  label={
                    <Typography variant="subtitle2">
                      Habilitar Apple Pay
                    </Typography>
                  }
                  sx={{ width: '100%' }}
                />
              </HtmlTooltip>
            }
            widthTitlePercentage={50}
            variant="compact"
            value={undefined}
          />
        </Stack>
      </Box>

      <Box>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
          Disputas e reembolsos
        </Typography>

        <Stack spacing={1.5}>
          <ListItemDefault
            title={
              <HtmlTooltip title="Quando ativado, o produtor vê a tela de Chargebacks, a aba Contestação nas transações e o aviso de disputas abertas.">
                <FormControlLabel
                  control={
                    <SwitchRoundSmall
                      sx={{ m: 1 }}
                      checked={
                        tenantSettings?.enableChargebackDispute ?? false
                      }
                      onChange={(e) => {
                        if (!tenantSettings) return
                        setTenantSettings({
                          ...tenantSettings,
                          enableChargebackDispute: e.target.checked,
                        })
                      }}
                    />
                  }
                  label={
                    <Typography variant="subtitle2">
                      Habilitar contestação de chargeback
                    </Typography>
                  }
                  sx={{ width: '100%' }}
                />
              </HtmlTooltip>
            }
            widthTitlePercentage={50}
            variant="compact"
            value={undefined}
          />

          <ListItemDefault
            title={
              <HtmlTooltip title="Libera o produtor a cancelar solicitações de reembolso de compras com 7+ dias, sem depender do cliente. Use apenas para produtores de confiança.">
                <FormControlLabel
                  control={
                    <SwitchRoundSmall
                      sx={{ m: 1 }}
                      checked={
                        tenantSettings?.enableRefundSelfCancel ?? false
                      }
                      onChange={(e) => {
                        if (!tenantSettings) return
                        setTenantSettings({
                          ...tenantSettings,
                          enableRefundSelfCancel: e.target.checked,
                        })
                      }}
                    />
                  }
                  label={
                    <Typography variant="subtitle2">
                      Permitir cancelar reembolso sozinho
                    </Typography>
                  }
                  sx={{ width: '100%' }}
                />
              </HtmlTooltip>
            }
            widthTitlePercentage={50}
            variant="compact"
            value={undefined}
          />
        </Stack>
      </Box>

      <Fade
        in={tenantSettings?.enableVelocityCheck ?? false}
        mountOnEnter
        unmountOnExit
      >
        <Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Regras de velocidade
          </Typography>

          <Stack spacing={1.5}>
            <ListItemDefault
              title="Máx. tentativas por cartão"
              widthTitlePercentage={50}
              variant="compact"
              value={
                <Box width="100%">
                  <OutlinedInput
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 1, max: 100 }}
                    value={tenantSettings?.maxCardAttemptsPerHour ?? 3}
                    onChange={handleChangeInt(
                      'maxCardAttemptsPerHour',
                      setErrorMaxCardAttemptsPerHour
                    )}
                    endAdornment={
                      <InfoInputAdornment title="Número máximo de tentativas de pagamento por cartão na janela de tempo configurada." />
                    }
                    error={errorMaxCardAttemptsPerHour !== ''}
                  />
                  {errorMaxCardAttemptsPerHour !== '' && (
                    <Typography variant="caption" color={red[700]}>
                      {errorMaxCardAttemptsPerHour}
                    </Typography>
                  )}
                </Box>
              }
            />

            <ListItemDefault
              title="Janela de tentativas (min)"
              widthTitlePercentage={50}
              variant="compact"
              value={
                <Box width="100%">
                  <OutlinedInput
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 1, max: 1440 }}
                    value={tenantSettings?.cardAttemptsWindowInMinutes ?? 60}
                    onChange={handleChangeInt(
                      'cardAttemptsWindowInMinutes',
                      setErrorCardAttemptsWindowInMinutes
                    )}
                    endAdornment={
                      <InfoInputAdornment title="Janela de tempo em minutos para contagem de tentativas por cartão." />
                    }
                    error={errorCardAttemptsWindowInMinutes !== ''}
                  />
                  {errorCardAttemptsWindowInMinutes !== '' && (
                    <Typography variant="caption" color={red[700]}>
                      {errorCardAttemptsWindowInMinutes}
                    </Typography>
                  )}
                </Box>
              }
            />

            <ListItemDefault
              title="Máx. transações por CPF/CNPJ"
              widthTitlePercentage={50}
              variant="compact"
              value={
                <Box width="100%">
                  <OutlinedInput
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 1, max: 1000 }}
                    value={
                      tenantSettings?.maxDocumentTransactionsPerDay ?? 5
                    }
                    onChange={handleChangeInt(
                      'maxDocumentTransactionsPerDay',
                      setErrorMaxDocumentTransactionsPerDay
                    )}
                    endAdornment={
                      <InfoInputAdornment title="Número máximo de transações por CPF/CNPJ na janela de tempo configurada." />
                    }
                    error={errorMaxDocumentTransactionsPerDay !== ''}
                  />
                  {errorMaxDocumentTransactionsPerDay !== '' && (
                    <Typography variant="caption" color={red[700]}>
                      {errorMaxDocumentTransactionsPerDay}
                    </Typography>
                  )}
                </Box>
              }
            />

            <ListItemDefault
              title="Janela de transações (horas)"
              widthTitlePercentage={50}
              variant="compact"
              value={
                <Box width="100%">
                  <OutlinedInput
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 1, max: 168 }}
                    value={
                      tenantSettings?.documentTransactionsWindowInHours ?? 24
                    }
                    onChange={handleChangeInt(
                      'documentTransactionsWindowInHours',
                      setErrorDocumentTransactionsWindowInHours
                    )}
                    endAdornment={
                      <InfoInputAdornment title="Janela em horas para contagem de transações por documento." />
                    }
                    error={errorDocumentTransactionsWindowInHours !== ''}
                  />
                  {errorDocumentTransactionsWindowInHours !== '' && (
                    <Typography variant="caption" color={red[700]}>
                      {errorDocumentTransactionsWindowInHours}
                    </Typography>
                  )}
                </Box>
              }
            />

            <ListItemDefault
              title="Recusas consecutivas p/ bloqueio"
              widthTitlePercentage={50}
              variant="compact"
              value={
                <Box width="100%">
                  <OutlinedInput
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 1, max: 20 }}
                    value={
                      tenantSettings?.consecutiveCardDeclinesToBlock ?? 2
                    }
                    onChange={handleChangeInt(
                      'consecutiveCardDeclinesToBlock',
                      setErrorConsecutiveCardDeclinesToBlock
                    )}
                    endAdornment={
                      <InfoInputAdornment title="Quantidade de recusas consecutivas do mesmo cartão antes do bloqueio temporário." />
                    }
                    error={errorConsecutiveCardDeclinesToBlock !== ''}
                  />
                  {errorConsecutiveCardDeclinesToBlock !== '' && (
                    <Typography variant="caption" color={red[700]}>
                      {errorConsecutiveCardDeclinesToBlock}
                    </Typography>
                  )}
                </Box>
              }
            />

            <ListItemDefault
              title="Duração do bloqueio (min)"
              widthTitlePercentage={50}
              variant="compact"
              value={
                <Box width="100%">
                  <OutlinedInput
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ min: 1, max: 1440 }}
                    value={tenantSettings?.cardTemporaryBlockMinutes ?? 30}
                    onChange={handleChangeInt(
                      'cardTemporaryBlockMinutes',
                      setErrorCardTemporaryBlockMinutes
                    )}
                    endAdornment={
                      <InfoInputAdornment title="Duração em minutos do bloqueio temporário do cartão após recusas consecutivas." />
                    }
                    error={errorCardTemporaryBlockMinutes !== ''}
                  />
                  {errorCardTemporaryBlockMinutes !== '' && (
                    <Typography variant="caption" color={red[700]}>
                      {errorCardTemporaryBlockMinutes}
                    </Typography>
                  )}
                </Box>
              }
            />

            <ListItemDefault
              title="Limite diário por cartão (R$)"
              widthTitlePercentage={50}
              variant="compact"
              value={
                <Box width="100%">
                  <OutlinedInput
                    fullWidth
                    size="small"
                    type="text"
                    value={maskMoney(
                      tenantSettings?.dailyCardAmountLimit ?? 2000
                    )}
                    onChange={handleChangeDailyCardAmountLimit}
                    endAdornment={
                      <InfoInputAdornment title="Limite de valor acumulado por cartão por dia em reais." />
                    }
                    error={errorDailyCardAmountLimit !== ''}
                  />
                  {errorDailyCardAmountLimit !== '' && (
                    <Typography variant="caption" color={red[700]}>
                      {errorDailyCardAmountLimit}
                    </Typography>
                  )}
                </Box>
              }
            />
          </Stack>
        </Box>
      </Fade>
    </Stack>
  )
}

export default UserSettingSecurity
